import { useEffect } from "react";
import { Navigate, Route, Routes, useLocation, useNavigationType } from "react-router";

import { AdminRoute, ProtectedRoute, StaffRoute } from "@/components/protected-route";
import { AdminActivityPage } from "@/features/admin/activity";
import { AdminUsersPage } from "@/features/admin/users";
import { AssessPage } from "@/features/assess";
import { AuthCallbackPage } from "@/features/auth-callback";
import { ExpertQueuePage } from "@/features/expert";
import { ExpertReviewPage } from "@/features/expert/detail";
import { HistoryPage } from "@/features/history";
import { LandingPage } from "@/features/landing";
import { PrivacyPolicyPage } from "@/features/legal/privacy-policy";
import { LoginPage } from "@/features/login";
import { NotFoundPage } from "@/features/not-found";
import { ProfilePage } from "@/features/profile";
import { ReportPage } from "@/features/report";
import { AcceptInvitePage } from "@/features/report/accept-invite";
import { SharedReportPage } from "@/features/report/shared";
import { SignupPage } from "@/features/signup";
import { VerifyEmailPage } from "@/features/verify-email";

/**
 * Restores the top-of-page scroll position on forward navigations. A Vite SPA
 * keeps the previous scroll offset across route changes (unlike Next.js, which
 * scrolls to top automatically), so a link followed from mid-page would land
 * the new page scrolled down. Back/forward (POP) is left alone so the browser
 * can restore the visitor's previous scroll position. Renders nothing.
 */
function ScrollToTop() {
  const { pathname } = useLocation();
  const navType = useNavigationType();
  useEffect(() => {
    if (navType !== "POP") {
      window.scrollTo(0, 0);
    }
  }, [pathname, navType]);
  return null;
}

export function App() {
  return (
    <>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<LandingPage />} />
        {/* The old template home page is gone; keep bookmarks working. */}
        <Route path="/home" element={<Navigate to="/" replace />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        {/* Public: linked from the footer and from the signup form, so it has to
            be readable before an account exists. */}
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/auth/callback" element={<AuthCallbackPage />} />
        <Route path="/assess" element={<AssessPage />} />
        <Route path="/report/:id" element={<ReportPage />} />
        {/* Public, anonymous, token-gated. The 3-segment path cannot collide
            with the 2-segment /report/:id above. */}
        <Route path="/report/shared/:token" element={<SharedReportPage />} />
        {/* Public route, but the invite still requires an account: the page
            self-gates on the preview's 401/403 rather than sitting inside
            <ProtectedRoute>, whose redirect to /login would strand the token. */}
        <Route path="/report/invite/:token" element={<AcceptInvitePage />} />
        <Route element={<ProtectedRoute />}>
          <Route path="/history" element={<HistoryPage />} />
          <Route path="/profile" element={<ProfilePage />} />
        </Route>
        <Route element={<StaffRoute />}>
          <Route path="/expert" element={<ExpertQueuePage />} />
          <Route path="/expert/:id" element={<ExpertReviewPage />} />
        </Route>
        <Route element={<AdminRoute />}>
          <Route path="/admin/users" element={<AdminUsersPage />} />
          <Route path="/admin/activity" element={<AdminActivityPage />} />
        </Route>
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </>
  );
}
